import { Checkbox, Group, Button, Stack } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pathnames } from 'routes/lcms';
import { CourseCombineData } from 'types/lcms/courseCombine';
import { columnCourseCombine } from './schemaCourseCombine';
import { useCourseCombineStore } from 'store/courseCombine';
import { useQuery } from '@tanstack/react-query';
import { trainingCatalogueService } from 'services/lcms/trainingCatalogue.service';
import { useDisclosure } from '@mantine/hooks';
import FilterCourseCombineTNA from './FilterCourseCombineTNA';

const TableCourseCombineTNA = () => {
  const navigate = useNavigate();
  const { setMode, setFormData } = useCourseCombineStore();
  const [filterOpened, { open: openFilterModal, close: closeFilterModal }] = useDisclosure();

  const LOCAL_KEY = 'courseCombineCompareIds';
  const [selectedIds, setSelectedIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [yearFilter, setYearFilter] = useState<number | undefined>(undefined);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      let next: string[];
      if (prev.includes(id)) {
        next = prev.filter((x) => x !== id);
      } else {
        if (prev.length >= 12) return prev; // do not exceed max 12
        next = [...prev, id];
      }
      localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
      return next;
    });
  };

  const clearSelections = () => {
    localStorage.removeItem(LOCAL_KEY);
    setSelectedIds([]);
  };

  const [baseColumns, setBaseColumns] = useState(() =>
    columnCourseCombine.filter((c: any) => c.id !== 'trainingRecommendationID')
  );

  const compareColumn = {
    heading: 'Compare',
    props: (row: CourseCombineData) => (
      <Checkbox
        aria-label={`select-${row.trainingRecommendationID}`}
        checked={selectedIds.includes(row.trainingRecommendationID)}
        onChange={(e) => {
          e.stopPropagation();
          toggleSelect(row.trainingRecommendationID);
        }}
      />
    ),
    sortable: false,
    active: true,
    disabled: false,
    id: 'compare'
  };

  const columns = [compareColumn, ...baseColumns];

  const handleSetColumns = (cols: any[]) => {
    const filtered = cols.filter((c) => c.id !== 'compare');
    setBaseColumns(filtered);
  };
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(10);
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search') || '';

  useEffect(() => {
    setPage(1);
  }, [search, yearFilter]);

  const { data: apiData, isPending } = useQuery({
    queryKey: ['trainingCatalogue', { page, size, search, year: yearFilter }],
    queryFn: () =>
      trainingCatalogueService.list({ pageNumber: page, pageSize: size, search, year: yearFilter })
  });

  const handleView = (row: CourseCombineData) => {
    setFormData(row);
    setMode('view');
    navigate(`${pathnames.courseCombineTNA}/${row.trainingRecommendationID}`);
  };

  return (
    <>
      <Card>
        <Stack>
          <Toolbar
            columns={columns}
            showFilter={true}
            onFilter={openFilterModal}
            showCreateButton={false}
            showFilterColumn
            setColumns={handleSetColumns}
            additionalButtons={
              <Group gap='xs'>
                <Button
                  variant='filled'
                  disabled={selectedIds.length < 2}
                  onClick={() => navigate(`${pathnames.courseCombineTNA}/compare`)}
                >
                  Compare ({selectedIds.length})
                </Button>
                {selectedIds.length > 0 && (
                  <Button variant='default' onClick={clearSelections}>
                    Clear Selections
                  </Button>
                )}
              </Group>
            }
          />
          <DataTables
            key={`${search}-${size}-${page}`}
            columns={columns}
            data={apiData?.data || []}
            loading={isPending}
            withBulkData={false}
            currentPage={page}
            currentSize={size}
            totalData={apiData?.totalRecords}
            handleView={handleView}
            handleChangePage={(p) => setPage(p)}
            handleChangeSize={(s) => setSize(s)}
          />
        </Stack>
      </Card>

      <FilterCourseCombineTNA
        opened={filterOpened}
        onClose={closeFilterModal}
        onSubmitForm={(data) => {
          setYearFilter(data.year);
        }}
        defaultData={{ year: yearFilter }}
      />
    </>
  );
};

export default TableCourseCombineTNA;
